import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {center} from '../../../../../libs/shared/ui/src/styles/mixins';
import {PaletteColorsEnum} from '../../../../../libs/shared/ui/src/styles/paletteModel';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {getHasNewIntegrationsDiscoverabilityP4} from '../../../../store/featureFlags/featureFlagsSelectors';
import {DraggableSwitcherItem, DraggableSwitcherItemPublicProps} from './draggableSwitcherItem';

/*
 * Props.
 */

interface PluginSwitcherItemProps extends DraggableSwitcherItemPublicProps {
  pluginName: string;
  pluginColor: PaletteColorsEnum;
  isInMultiViewButton?: boolean;
}

/*
 * Style.
 */

interface WrapperStyleProps {
  $isDragging: boolean;
  $isSelected?: boolean;
  $isInMultiViewButton?: boolean;
  $pluginColor: PaletteColorsEnum;
  $isNewIntDiscoveryEnabled: boolean;
}
const StyledWrapperDiv = styled.div<WrapperStyleProps>`
  width: 100%;
  height: 100%;
  ${center()};

  ${(p) =>
    p.$isNewIntDiscoveryEnabled &&
    css`
      color: ${p.theme.palette[p.$pluginColor].shade40};
    `};

  ${(p) =>
    !p.$isNewIntDiscoveryEnabled &&
    css`
      color: ${p.theme.greys.shade60};
      ${maybeAddSelectedStyle(p)};
    `};
`;

function maybeAddSelectedStyle({
  $isDragging,
  $isSelected,
  $pluginColor,
  $isInMultiViewButton,
}: WrapperStyleProps) {
  if ($isInMultiViewButton && !$isSelected) {
    return css`
      color: ${(p) => p.theme.greys.shade60};
    `;
  }

  if ($isDragging) {
    return css`
      color: ${(p) => p.theme.palette[$pluginColor].shade40};
    `;
  }

  if ($isSelected) {
    return css`
      color: ${(p) => p.theme.greys.white};
      background-color: ${(p) => p.theme.palette[$pluginColor].shade40};
    `;
  }

  return css`
    &:hover {
      color: ${(p) => p.theme.palette[$pluginColor].shade40};
    }
  `;
}

const StyledInitialDiv = styled.div`
  font-size: ${fontSizes.veryLarge};
  font-weight: ${fontWeights.bold};
  text-transform: uppercase;
  margin-bottom: 1px;
`;

/*
 * Component.
 */

export const PluginSwitcherItem: FC<PluginSwitcherItemProps> = (props) => {
  const isNewIntDiscoveryEnabled = useAppSelector(getHasNewIntegrationsDiscoverabilityP4);
  return (
    <DraggableSwitcherItem {...props} renderTooltip={() => props.pluginName}>
      {(isDragging) => (
        <StyledWrapperDiv
          $isDragging={isDragging}
          $isSelected={props.isSelected}
          $isInMultiViewButton={props.isInMultiViewButton}
          $pluginColor={props.pluginColor}
          $isNewIntDiscoveryEnabled={isNewIntDiscoveryEnabled}
        >
          <StyledInitialDiv>{props.pluginName[0] || 'P'}</StyledInitialDiv>
        </StyledWrapperDiv>
      )}
    </DraggableSwitcherItem>
  );
};
